// Decision 5 for the FORWARD USD CAGR: a projection must never look like a
// measurement, and absent forecast data must never look like a result.
//
// It branches on insufficient_forecast_data FIRST — leading with the "no
// forecast" message before any table — because a 0% that reached scoring as a
// forced zero (too few forecast years) must not be dressed up as a row of
// numbers. When there IS enough data it shows, per projected year, the projected
// rate and projected USD value, plus the depreciation rate and base year the
// projection rests on, all unmistakably marked as a PROJECTION.
//
// Field names are the ones the engine writes for CAGR_USD_FUTURE
// (functions/index.js): year_end_rate (the PROJECTED rate), value_usd, plus
// depreciation (a fraction) and base_year.
export default function FutureUsdCagrNarration({ sv }) {
  const cur = sv.reporting_currency || '';

  // (a) INSUFFICIENT FORECAST DATA — this branch renders FIRST and returns,
  // suppressing the year table entirely.
  if (sv.insufficient_forecast_data) {
    const n = sv.consecutive_years_found || 0;
    return (
      <div style={{ fontSize: 12, color: 'var(--color-warning)', marginBottom: 4 }}>
        <p style={{ marginBottom: 0 }}>
          <strong>
            Insufficient forecast data — 0% returned (only {n} consecutive
            {' '}year{n === 1 ? '' : 's'} provided).
          </strong>{' '}
          At least 3 consecutive projected years are required. This 0% reflects
          missing data, not a flat forecast, and is scored 0.
        </p>
      </div>
    );
  }

  // (b) A REAL PROJECTION — lead with the assumption, then the per-year detail.
  const used = Array.isArray(sv.years_used) ? sv.years_used : [];
  const dropped = Array.isArray(sv.years_dropped) ? sv.years_dropped : [];
  const num = v => (typeof v === 'number' ? v.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  }) : '—');
  const depPct = typeof sv.depreciation === 'number'
    ? (sv.depreciation * 100).toFixed(1) : null;

  return (
    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
      <p style={{ marginBottom: 4 }}>
        <strong>PROJECTION, not a measurement.</strong> Each projected year is
        converted to USD at a projected year-end rate
        {cur ? ` for ${cur}` : ''}
        {sv.base_year != null
          ? `, projected forward from ${cur} ${sv.base_year} (${sv.base_rate})`
          : ''}
        {depPct != null ? ` at ${depPct}% annual depreciation` : ''}
        {typeof sv.actual_periods === 'number'
          ? ` — ${sv.actual_periods} period${sv.actual_periods === 1 ? '' : 's'}`
          : ''}.
      </p>
      {used.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 340 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '2px 10px 2px 0' }}>Year (projected)</th>
                <th style={{ padding: '2px 10px 2px 0' }}>{cur || 'Reporting'}</th>
                <th style={{ padding: '2px 10px 2px 0' }}>Projected rate</th>
                <th style={{ padding: '2px 0' }}>USD (projected)</th>
              </tr>
            </thead>
            <tbody>
              {used.map(y => (
                <tr key={y.fiscal_year}>
                  <td style={{ padding: '2px 10px 2px 0' }}>{y.fiscal_year}</td>
                  <td style={{ padding: '2px 10px 2px 0', fontVariantNumeric: 'tabular-nums' }}>{num(y.value_reporting)}</td>
                  <td style={{ padding: '2px 10px 2px 0', fontVariantNumeric: 'tabular-nums' }}>{y.year_end_rate ?? '—'}</td>
                  <td style={{ padding: '2px 0', fontVariantNumeric: 'tabular-nums' }}>{num(y.value_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dropped.length > 0 && (
        <p style={{ marginTop: 4 }}>
          Excluded:{' '}
          {dropped.map((d, i) => (
            <span key={d.fiscal_year ?? d.question_id ?? i}>
              {i > 0 ? '; ' : ''}
              {d.fiscal_year ?? 'a year'} ({d.reason})
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
